/**
 * Akarya Box simple mode (docs/box/PLAN.md A.7). `GET /api/capabilities`
 * turns the features a LAN box never has off and sets `simple`; the server
 * refuses the same routes (`App\Support\BoxHidden`). This is what the console
 * derives from it: which screens are reachable, and what the sidebar shows.
 */

import type { ServerCapabilities } from './types.js';

/** Console areas that need a capability, by route prefix. */
export const GATED_ROUTES: { prefix: string; flag: keyof ServerCapabilities }[] = [
  { prefix: '/settings/sso', flag: 'sso' },
  { prefix: '/settings/billing', flag: 'billing' },
  { prefix: '/settings/roles', flag: 'roles' },
  { prefix: '/settings/api-tokens', flag: 'apiTokens' },
  { prefix: '/settings/webhooks', flag: 'webhooks' },
  { prefix: '/settings/branding', flag: 'branding' },
  { prefix: '/settings/impersonate', flag: 'impersonation' },
  { prefix: '/licenses', flag: 'portableLicenseSales' },
];

function underPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** Whether a console path may be opened under these capabilities (`null` = still loading). */
export function isRouteAvailable(path: string, caps: ServerCapabilities | null): boolean {
  if (caps === null) return true;
  const gate = GATED_ROUTES.find((g) => underPrefix(path, g.prefix));
  return gate === undefined || caps[gate.flag] !== false;
}

/** Sidebar entries a box keeps, in order; everything else is reached from the home or by URL. */
export const SIMPLE_NAV_PATHS = ['/', '/displays', '/layouts', '/media', '/sos', '/settings/users', '/settings/backup', '/settings/audit'];

export function showsInNav(path: string, caps: ServerCapabilities | null): boolean {
  return caps?.simple !== true || SIMPLE_NAV_PATHS.includes(path);
}

/** The box home: one tap to each place. `scan` opens the camera straight away. */
export const HOME_TILES: { to: string; label: string; hint: string }[] = [
  { to: '/claim?scan=1', label: 'Add screen', hint: 'Scan the code on a new screen' },
  { to: '/displays', label: 'Screens', hint: 'What is playing where' },
  { to: '/layouts', label: 'Layouts', hint: 'Design what screens show' },
  { to: '/media', label: 'Media', hint: 'Pictures and videos' },
  { to: '/sos', label: 'SOS', hint: 'Emergency message on every screen' },
];
